import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactModal from '../components/ContactModal';
import { getPortfolioItems, getPortfolioCategories, portfolioCategoryKeys } from '../data/portfolioData';
import { Heart, Calendar } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Portfolio = () => {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Get translated portfolio items and categories
  const portfolioItems = getPortfolioItems(t);
  const categoryTranslations = getPortfolioCategories(t);

  const filteredItems = selectedCategory === 'All'
    ? portfolioItems
    : portfolioItems.filter(item => item.category === categoryTranslations[selectedCategory]);

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Breadcrumb */}
      <div className="bg-gray-100 py-4">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-sm text-gray-600">
            <a href="/" className="text-blue-500 hover:underline">AP Pacific</a> &gt; {t('portfolio.breadcrumb')}
          </div>
        </div>
      </div>

      {/* Page Header */}
      <div className="bg-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">{t('portfolio.pageTitle')}</h1>
          <p className="text-lg text-gray-600">
            {t('portfolio.description')}
          </p>
        </div>
      </div>

      {/* Filter Categories */}
      <section className="py-6 sm:py-8 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-2 sm:gap-4">
            {portfolioCategoryKeys.map((categoryKey, index) => (
              <button
                key={index}
                onClick={() => setSelectedCategory(categoryKey)}
                className={`px-3 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-medium transition-colors ${
                  selectedCategory === categoryKey
                    ? 'bg-blue-500 text-white'
                    : 'bg-white text-gray-700 hover:bg-blue-100'
                }`}
              >
                {categoryTranslations[categoryKey]}
              </button>
            ))}
          </div>

          <div className="text-center mt-4 text-gray-600 text-sm sm:text-base">
            {t('portfolio.showing')} {filteredItems.length} {filteredItems.length !== 1 ? t('portfolio.projects') : t('portfolio.project')}
            {selectedCategory !== 'All' && ` ${t('portfolio.in')} ${categoryTranslations[selectedCategory]}`}
          </div>
        </div>
      </section>

      {/* Portfolio Grid */}
      <section className="py-8 sm:py-12 lg:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {filteredItems.map((item) => (
              <div key={item.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow group">
                <div className="relative overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-40 sm:h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                
                <div className="p-4 sm:p-6">
                  <div className="flex items-center justify-between mb-3 text-xs sm:text-sm text-gray-500">
                    <div className="flex items-center">
                      <Calendar className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                      {item.date}
                    </div>
                    <div className="flex items-center">
                      <Heart className="w-3 h-3 sm:w-4 sm:h-4 mr-1 text-red-500" />
                      {item.likes}
                    </div>
                  </div>
                  
                  <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-2 leading-tight">
                    {item.title}
                  </h3>
                  
                  <p className="text-gray-600 mb-4 line-clamp-3 text-sm sm:text-base">
                    {item.description}
                  </p>
                  
                  <a
                    href={`/portfolio-item/${item.id}`}
                    className="inline-block bg-blue-500 text-white px-4 sm:px-6 py-2 rounded hover:bg-blue-600 transition-colors text-sm sm:text-base"
                  >
                    {t('portfolio.readMore')}
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-blue-500 text-white text-center">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold mb-4">
            {t('portfolio.ctaTitle')}
          </h2>
          <p className="text-xl mb-8">
            {t('portfolio.ctaDescription')}
          </p>
          <button
            onClick={() => setIsModalOpen(true)}
            className="bg-yellow-400 text-blue-900 font-bold px-8 py-3 rounded-full hover:bg-yellow-300 transition-colors"
          >
            {t('portfolio.ctaButton')}
          </button>
        </div>
      </section>

      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      <Footer />
    </div>
  );
};

export default Portfolio;